import React from 'react';
import Spinner from './Spinner';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize    = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const sizeMap: Record<ButtonSize, string> = {
  xs: 'px-2.5 py-1 text-[10px] rounded-lg gap-1',
  sm: 'px-3 py-1.5 text-xs rounded-xl gap-1.5',
  md: 'px-4 py-2.5 text-sm rounded-xl gap-2',
  lg: 'px-6 py-3 text-sm rounded-xl gap-2',
};

const variantMap: Record<ButtonVariant, string> = {
  // Amarillo con texto oscuro — acción principal
  primary:
    'bg-[#FACF52] text-[#3B4155] font-semibold hover:opacity-90 ' +
    'dark:bg-[#FACF52] dark:text-[#3B4155]',

  // Fondo suave con texto oscuro — acción secundaria / cancelar
  secondary:
    'bg-brand-light text-brand-dark font-semibold hover:opacity-80 ' +
    'dark:bg-[#252840] dark:text-[#8891B8]',

  // Rojo — acción destructiva
  danger:
    'bg-red-500 text-white font-semibold hover:bg-red-600',

  // Sin fondo — acción terciaria / links
  ghost:
    'text-brand-mid font-medium hover:text-brand-dark hover:bg-brand-light',
};

/**
 * Botón unificado de la aplicación.
 *
 * @example
 * <Button variant="primary" onClick={handleSave}>Guardar</Button>
 * <Button variant="secondary" onClick={handleCancel}>Cancelar</Button>
 * <Button variant="danger"   onClick={handleDelete}>Eliminar</Button>
 * <Button variant="primary" loading>Guardando...</Button>
 */
const Button: React.FC<ButtonProps> = ({
  variant  = 'secondary',
  size     = 'md',
  loading  = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  disabled,
  children,
  className = '',
  ...rest
}) => {
  const base = [
    'inline-flex items-center justify-center transition-all duration-150 select-none',
    'focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:ring-offset-1',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    sizeMap[size],
    variantMap[variant],
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button disabled={disabled || loading} className={base} {...rest}>
      {loading ? (
        <Spinner
          size="sm"
          color={variant === 'primary' ? 'dark' : variant === 'danger' ? 'white' : 'mid'}
        />
      ) : (
        leftIcon
      )}
      {children}
      {!loading && rightIcon}
    </button>
  );
};

export default Button;
